/**
 * Boost AI Search & Filter - UL/LI to Select/Option Converter
 *
 * This script converts Boost AI Search & Filter ul/li filter options
 * with class "boost-pfs-filter-option-item-list-single-list" to select/option elements.
 *
 * Features:
 * - Converts ul/li to select/option on page load and filter updates
 * - Maintains default "none selected" state
 * - Preserves all original functionality
 * - Handles clear filter actions
 */

(function () {
  "use strict";

  // Configuration
  const CONFIG = {
    targetClass: "boost-pfs-filter-option-item-list-single-list",
    selectClass: "boost-pfs-filter-select-converted",
    defaultOptionText: "Select an option",
    defaultOptionValue: "",
    observerConfig: {
      childList: true,
      subtree: true,
      attributes: false,
    },
  };

  // Track converted elements to avoid re-conversion
  const convertedElements = new WeakSet();

  /**
   * Convert a ul/li structure to select/option
   * @param {HTMLElement} ulElement - The ul element to convert
   */
  function convertUlToSelect(ulElement) {
    if (convertedElements.has(ulElement)) {
      return;
    }

    // Get the parent container and filter option
    const filterOption = ulElement.closest(".boost-pfs-filter-option");
    if (!filterOption) return;

    // Get the filter title
    const titleElement = filterOption.querySelector(
      ".boost-pfs-filter-option-title-text, .boost-pfs-filter-option-title h3"
    );
    const filterTitle = titleElement
      ? titleElement.textContent.trim()
      : "Filter";

    // Create select element
    const selectElement = document.createElement("select");
    selectElement.className = `boost-pfs-filter-select ${CONFIG.selectClass}`;
    selectElement.setAttribute("data-filter-type", "select");
    selectElement.setAttribute("aria-label", filterTitle);
    selectElement.setAttribute(
      "data-original-ul-id",
      ulElement.id || "ul-" + Date.now()
    );

    // Add default option
    const defaultOption = document.createElement("option");
    defaultOption.value = CONFIG.defaultOptionValue;
    defaultOption.textContent = CONFIG.defaultOptionText;
    defaultOption.selected = true;
    selectElement.appendChild(defaultOption);

    // Convert li elements to option elements
    const liElements = ulElement.querySelectorAll("li");
    let hasSelectedOption = false;

    liElements.forEach((li) => {
      const button = li.querySelector("button, .boost-pfs-filter-button");
      if (!button) return;

      const option = document.createElement("option");

      // Get the value from button's data attributes or text
      const value =
        button.getAttribute("data-value") ||
        button.getAttribute("data-filter-value") ||
        button.getAttribute("data-url") ||
        button.textContent.trim();

      option.value = value;
      option.textContent = button.textContent.trim();

      // Check if this option is currently selected
      const isSelected =
        button.getAttribute("aria-checked") === "true" ||
        button.classList.contains("boost-pfs-filter-button-selected") ||
        li.classList.contains("boost-pfs-filter-option-item-selected") ||
        button.classList.contains("selected");

      if (isSelected) {
        option.selected = true;
        defaultOption.selected = false;
        hasSelectedOption = true;
      }

      selectElement.appendChild(option);
    });

    // If no option is selected, ensure default is selected
    if (!hasSelectedOption) {
      defaultOption.selected = true;
    }

    // Handle select change events
    selectElement.addEventListener("change", function (e) {
      const selectedValue = e.target.value;

      if (selectedValue === CONFIG.defaultOptionValue) {
        // Clear filter - find and click the clear button
        const clearButton = filterOption.querySelector(
          ".boost-pfs-filter-clear"
        );
        if (clearButton) {
          clearButton.click();
        } else {
          // If no clear button, try to find and click the first option to reset
          const currentLiElements = ulElement.querySelectorAll("li");
          const firstLi = currentLiElements[0];
          if (firstLi) {
            const firstButton = firstLi.querySelector(
              "button, .boost-pfs-filter-button"
            );
            if (
              firstButton &&
              firstButton.getAttribute("aria-checked") === "true"
            ) {
              firstButton.click(); // This should deselect it
            }
          }
        }
      } else {
        // Always get current li elements (including dynamically loaded ones)
        const currentLiElements = ulElement.querySelectorAll("li");

        // Find the corresponding li button and click it
        const correspondingLi = Array.from(currentLiElements).find((li) => {
          const button = li.querySelector("button, .boost-pfs-filter-button");
          if (!button) return false;

          const buttonValue =
            button.getAttribute("data-value") ||
            button.getAttribute("data-filter-value") ||
            button.getAttribute("data-url") ||
            button.textContent.trim();

          return buttonValue === selectedValue;
        });

        if (correspondingLi) {
          const button = correspondingLi.querySelector(
            "button, .boost-pfs-filter-button"
          );
          if (button) {
            // First deselect any currently selected option
            const currentlySelected = filterOption.querySelector(
              '.boost-pfs-filter-button[aria-checked="true"]'
            );
            if (currentlySelected && currentlySelected !== button) {
              currentlySelected.click();
            }

            // Then select the new option
            if (button.getAttribute("aria-checked") !== "true") {
              button.click();
            }
          }
        }
      }
    });

    // Store reference to original ul for potential restoration and updates
    ulElement.classList.add("boost-pfs-converted");
    ulElement.setAttribute("data-converted", "true");

    // Hide the ul but keep it functional for Boost AI scroll detection
    ulElement.classList.add("boost-pfs-hidden-ul");

    // Insert select element after the ul
    ulElement.parentNode.insertBefore(selectElement, ulElement.nextSibling);

    // Mark as converted
    convertedElements.add(ulElement);

    // Function to force load all values by simulating scroll events
    function forceLoadAllValues() {
      // Simulate scroll events to trigger Boost AI's value loading
      let scrollAttempts = 0;
      const maxScrollAttempts = 5;
      let lastLiCount = 0;

      const scrollInterval = setInterval(() => {
        scrollAttempts++;
        const currentLiCount = ulElement.querySelectorAll("li").length;

        // Scroll to bottom to trigger loading
        ulElement.scrollTop = ulElement.scrollHeight;

        // Also trigger scroll event manually
        const scrollEvent = new Event("scroll", { bubbles: true });
        ulElement.dispatchEvent(scrollEvent);

        // Update select options
        updateExistingSelects();

        // Stop if no new values are being added or max attempts reached
        if (
          scrollAttempts >= maxScrollAttempts ||
          (currentLiCount === lastLiCount && scrollAttempts > 3)
        ) {
          clearInterval(scrollInterval);
        }

        lastLiCount = currentLiCount;
      }, 600);
    }

    // Set up periodic check for new options (stops after initial load)
    let periodicCheckCount = 0;
    const maxPeriodicChecks = 20; // Stop after 20 checks (20 seconds)

    const checkForNewOptions = setInterval(function () {
      if (!document.contains(ulElement) || !document.contains(selectElement)) {
        clearInterval(checkForNewOptions);
        return;
      }

      periodicCheckCount++;
      const currentLiCount = ulElement.querySelectorAll("li").length;
      const currentOptionCount = selectElement.options.length - 1; // Exclude default option

      if (currentLiCount > currentOptionCount) {
        updateExistingSelects();
      }

      // Stop periodic checks after max attempts or if values are stable
      if (
        periodicCheckCount >= maxPeriodicChecks ||
        (currentLiCount === currentOptionCount && periodicCheckCount > 5)
      ) {
        clearInterval(checkForNewOptions);
      }
    }, 1000);

    // Force load all values after a short delay
    setTimeout(() => {
      forceLoadAllValues();
    }, 2000);

    // Hook into Boost AI's afterRender callback for automatic updates
    if (
      typeof FilterResult !== "undefined" &&
      FilterResult.prototype.afterRender
    ) {
      const originalAfterRender = FilterResult.prototype.afterRender;
      FilterResult.prototype.afterRender = function (data, eventType) {
        if (originalAfterRender) {
          originalAfterRender.call(this, data, eventType);
        }
        setTimeout(() => updateExistingSelects(), 100);
      };
    }
  }

  /**
   * Find and convert all target ul elements
   */
  function convertAllTargetElements() {
    const targetElements = document.querySelectorAll(
      `ul.${CONFIG.targetClass}`
    );
    targetElements.forEach(convertUlToSelect);
  }

  /**
   * Handle filter updates and re-conversion
   */
  function handleFilterUpdates() {
    // Use MutationObserver to watch for changes in the filter tree
    const filterTree = document.querySelector(
      ".boost-pfs-filter-tree, #boost-pfs-filter-tree"
    );
    if (!filterTree) return;

    const observer = new MutationObserver(function (mutations) {
      let shouldUpdate = false;

      mutations.forEach(function (mutation) {
        if (mutation.type === "childList") {
          // Check if any added nodes contain our target class or new li elements
          mutation.addedNodes.forEach(function (node) {
            if (node.nodeType === Node.ELEMENT_NODE) {
              // Check for new ul elements with target class
              if (
                node.classList &&
                node.classList.contains(CONFIG.targetClass)
              ) {
                shouldUpdate = true;
              } else if (
                node.querySelector &&
                node.querySelector(`.${CONFIG.targetClass}`)
              ) {
                shouldUpdate = true;
              }
              // Check for new li elements being added to existing ul
              else if (
                node.tagName === "LI" &&
                node.closest(`.${CONFIG.targetClass}`)
              ) {
                shouldUpdate = true;
              }
            }
          });
        }
      });

      if (shouldUpdate) {
        // Small delay to ensure DOM is fully updated
        setTimeout(function () {
          convertAllTargetElements();
          updateExistingSelects();
        }, 100);
      }
    });

    observer.observe(filterTree, CONFIG.observerConfig);
  }

  /**
   * Update existing select elements with new options from hidden ul elements
   */
  function updateExistingSelects() {
    const convertedSelects = document.querySelectorAll(
      `.${CONFIG.selectClass}`
    );

    convertedSelects.forEach((select) => {
      const filterOption = select.closest(".boost-pfs-filter-option");

      if (filterOption) {
        // Find the hidden ul element that corresponds to this select
        const hiddenUl = filterOption.querySelector(
          `.${CONFIG.targetClass}.boost-pfs-converted`
        );

        if (hiddenUl) {
          // Get current options in select (excluding default option)
          const currentOptions = Array.from(select.options)
            .slice(1)
            .map((opt) => opt.value);

          // Get all li elements from the hidden ul
          const liElements = hiddenUl.querySelectorAll("li");
          const newOptions = [];

          liElements.forEach((li) => {
            const button = li.querySelector("button, .boost-pfs-filter-button");
            if (button) {
              // Get the text from the span with class boost-pfs-filter-option-value
              const valueSpan = button.querySelector(
                ".boost-pfs-filter-option-value"
              );
              const value = valueSpan
                ? valueSpan.textContent.trim()
                : button.textContent.trim();

              newOptions.push({
                value: value,
                text: value,
                selected:
                  button.getAttribute("aria-checked") === "true" ||
                  button.classList.contains(
                    "boost-pfs-filter-button-selected"
                  ) ||
                  li.classList.contains(
                    "boost-pfs-filter-option-item-selected"
                  ) ||
                  button.classList.contains("selected"),
              });
            }
          });

          // Remove options that no longer exist in the ul
          const ulOptionValues = newOptions.map((opt) => opt.value);
          const optionsToRemove = [];
          for (let i = 1; i < select.options.length; i++) {
            // Skip default option
            if (!ulOptionValues.includes(select.options[i].value)) {
              optionsToRemove.push(select.options[i]);
            }
          }
          optionsToRemove.forEach((option) => option.remove());

          // Add new options that don't exist yet
          let hasNewOptions = false;
          newOptions.forEach((newOption) => {
            if (!currentOptions.includes(newOption.value)) {
              const option = document.createElement("option");
              option.value = newOption.value;
              option.textContent = newOption.text;

              if (newOption.selected) {
                option.selected = true;
                // Unselect default option
                select.options[0].selected = false;
              }

              select.appendChild(option);
              hasNewOptions = true;
            }
          });

          // Update selection state for existing options
          newOptions.forEach((newOption) => {
            const existingOption = Array.from(select.options).find(
              (opt) => opt.value === newOption.value
            );
            if (existingOption) {
              if (newOption.selected) {
                existingOption.selected = true;
                select.options[0].selected = false; // Unselect default
              }
            }
          });

          if (hasNewOptions) {
          }
        }
      }
    });
  }

  /**
   * Handle clear filter actions
   */
  function handleClearFilters() {
    // Listen for clear filter button clicks
    document.addEventListener("click", function (e) {
      // Check if clear filter button was clicked
      if (
        e.target.classList.contains("boost-pfs-filter-clear") ||
        e.target.classList.contains("boost-pfs-filter-clear-all")
      ) {
        // Reset all converted selects to default option
        setTimeout(function () {
          const convertedSelects = document.querySelectorAll(
            `.${CONFIG.selectClass}`
          );
          convertedSelects.forEach((select) => {
            select.selectedIndex = 0; // Select the default option
          });
        }, 200);
      }
    });

    // Listen for Boost AI Search & Filter events
    document.addEventListener("boost-pfs-filter-updated", function () {
      // Reset all converted selects when filters are updated
      setTimeout(function () {
        const convertedSelects = document.querySelectorAll(
          `.${CONFIG.selectClass}`
        );
        convertedSelects.forEach((select) => {
          // Check if any option should be selected based on current filter state
          const filterOption = select.closest(".boost-pfs-filter-option");
          if (filterOption) {
            const selectedButton = filterOption.querySelector(
              '.boost-pfs-filter-button[aria-checked="true"]'
            );
            if (selectedButton) {
              const buttonValue =
                selectedButton.getAttribute("data-value") ||
                selectedButton.getAttribute("data-filter-value") ||
                selectedButton.getAttribute("data-url") ||
                selectedButton.textContent.trim();

              // Find and select the corresponding option
              const options = select.querySelectorAll("option");
              for (let option of options) {
                if (option.value === buttonValue) {
                  option.selected = true;
                  break;
                }
              }
            } else {
              // No option selected, reset to default
              select.selectedIndex = 0;
            }
          }
        });
      }, 100);
    });

    // Listen for URL changes (back/forward navigation)
    window.addEventListener("popstate", function () {
      setTimeout(function () {
        const convertedSelects = document.querySelectorAll(
          `.${CONFIG.selectClass}`
        );
        convertedSelects.forEach((select) => {
          const filterOption = select.closest(".boost-pfs-filter-option");
          if (filterOption) {
            const selectedButton = filterOption.querySelector(
              '.boost-pfs-filter-button[aria-checked="true"]'
            );
            if (selectedButton) {
              const buttonValue =
                selectedButton.getAttribute("data-value") ||
                selectedButton.getAttribute("data-filter-value") ||
                selectedButton.getAttribute("data-url") ||
                selectedButton.textContent.trim();

              const options = select.querySelectorAll("option");
              for (let option of options) {
                if (option.value === buttonValue) {
                  option.selected = true;
                  break;
                }
              }
            } else {
              select.selectedIndex = 0;
            }
          }
        });
      }, 100);
    });
  }

  /**
   * Handle scroll-based loading and other dynamic content updates
   */
  function handleScrollAndDynamicLoading() {
    // Monitor for scroll events in filter containers
    document.addEventListener(
      "scroll",
      function (e) {
        const filterContainers = document.querySelectorAll(
          ".boost-pfs-filter-option-content, .boost-pfs-filter-tree, .boost-pfs-filter-option"
        );
        filterContainers.forEach((container) => {
          if (container.contains(e.target) || container === e.target) {
            // Debounce the update check
            clearTimeout(container._scrollTimeout);
            container._scrollTimeout = setTimeout(function () {
              updateExistingSelects();
            }, 300);
          }
        });
      },
      true
    );

    // Also monitor for scroll events on the window (in case the filter is in a scrollable area)
    window.addEventListener("scroll", function () {
      // Check if we're near the bottom of any filter option
      const filterOptions = document.querySelectorAll(
        ".boost-pfs-filter-option"
      );
      filterOptions.forEach((option) => {
        const rect = option.getBoundingClientRect();
        // If the filter option is visible and near the bottom of the viewport
        if (rect.top < window.innerHeight && rect.bottom > 0) {
          clearTimeout(option._scrollTimeout);
          option._scrollTimeout = setTimeout(function () {
            updateExistingSelects();
          }, 500);
        }
      });
    });

    // Monitor scroll events on the actual ul elements (now that they're visible but transparent)
    document.addEventListener(
      "scroll",
      function (e) {
        // Check if the scroll target is a converted ul element
        if (
          e.target.classList &&
          e.target.classList.contains("boost-pfs-converted")
        ) {
          clearTimeout(e.target._scrollTimeout);
          e.target._scrollTimeout = setTimeout(function () {
            updateExistingSelects();
          }, 200);
        }
      },
      true
    );

    // Also monitor for scroll events on any element that contains our converted ul elements
    document.addEventListener(
      "scroll",
      function (e) {
        const convertedUls = document.querySelectorAll(".boost-pfs-converted");
        convertedUls.forEach((ul) => {
          if (ul.contains(e.target) || ul === e.target) {
            clearTimeout(ul._scrollTimeout);
            ul._scrollTimeout = setTimeout(function () {
              updateExistingSelects();
            }, 200);
          }
        });
      },
      true
    );

    // Monitor for Boost AI specific events
    const boostEvents = [
      "boost-pfs-filter-updated",
      "boost-pfs-filter-loaded",
      "boost-pfs-filter-refreshed",
      "boost-pfs-filter-options-loaded",
    ];

    boostEvents.forEach((eventName) => {
      document.addEventListener(eventName, function () {
        setTimeout(function () {
          convertAllTargetElements();
          updateExistingSelects();
        }, 200);
      });
    });

    // Monitor for AJAX requests completion (common pattern for dynamic loading)
    const originalFetch = window.fetch;
    window.fetch = function (...args) {
      return originalFetch.apply(this, args).then((response) => {
        // Check if this might be a Boost AI request
        if (
          args[0] &&
          typeof args[0] === "string" &&
          (args[0].includes("boost-pfs") || args[0].includes("bc-sf-filter"))
        ) {
          setTimeout(function () {
            updateExistingSelects();
          }, 500);
        }
        return response;
      });
    };

    // Monitor for intersection observer (common for infinite scroll)
    if ("IntersectionObserver" in window) {
      const observer = new IntersectionObserver(
        function (entries) {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              // Check if this is a filter option container
              const filterOption = entry.target.closest(
                ".boost-pfs-filter-option"
              );
              if (filterOption) {
                setTimeout(function () {
                  updateExistingSelects();
                }, 200);
              }
            }
          });
        },
        {
          rootMargin: "100px", // Trigger 100px before the element comes into view
        }
      );

      // Observe all filter option containers
      document
        .querySelectorAll(".boost-pfs-filter-option")
        .forEach((option) => {
          observer.observe(option);
        });
    }
  }

  /**
   * Initialize the converter
   */
  function init() {
    // Wait for DOM to be ready
    if (document.readyState === "loading") {
      document.addEventListener("DOMContentLoaded", function () {
        setTimeout(init, 500); // Additional delay for Boost AI to initialize
      });
      return;
    }

    // Wait for Boost AI Search & Filter to initialize
    const checkBoostAI = setInterval(function () {
      const filterTree = document.querySelector(
        ".boost-pfs-filter-tree, #boost-pfs-filter-tree"
      );
      if (filterTree) {
        clearInterval(checkBoostAI);

        // Initial conversion
        convertAllTargetElements();

        // Set up observers and handlers
        handleFilterUpdates();
        handleClearFilters();
        handleScrollAndDynamicLoading();
      }
    }, 100);

    // Timeout after 10 seconds
    setTimeout(function () {
      clearInterval(checkBoostAI);
    }, 10000);
  }

  // Start the converter
  init();

  // Expose a global function for manual updates (useful for debugging)
  window.boostPFSUpdateSelects = function () {
    updateExistingSelects();
  };

  // Also expose a function to check the status
  window.boostPFSCheckStatus = function () {
    const convertedSelects = document.querySelectorAll(
      `.${CONFIG.selectClass}`
    );

    convertedSelects.forEach((select, index) => {
      const filterOption = select.closest(".boost-pfs-filter-option");
      const filterTitle = filterOption
        ? filterOption.querySelector(
            ".boost-pfs-filter-option-title-text, .boost-pfs-filter-option-title h3"
          )?.textContent
        : "Unknown";
      const hiddenUl = filterOption
        ? filterOption.querySelector(
            `.${CONFIG.targetClass}.boost-pfs-converted`
          )
        : null;
      const liCount = hiddenUl ? hiddenUl.querySelectorAll("li").length : 0;
      const optionCount = select.options.length - 1; // Exclude default option
    });
  };
})();
